import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Barcode } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function BarcodeScanDialog({
  open,
  onOpenChange,
  onDetected,
  title = "Scan a barcode",
  placeholder = "Barcode or serial #",
  testPrefix = "po-barcode",
}) {
  const REGION_ID = `${testPrefix}-region`;
  const [manual, setManual] = useState("");
  const [camError, setCamError] = useState("");
  const scannerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setManual("");
    setCamError("");
    let cancelled = false;

    const start = async () => {
      try {
        const scanner = new Html5Qrcode(REGION_ID, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 160 } },
          (text) => {
            if (!cancelled) onDetected((text || "").trim());
          },
          () => {},
        );
      } catch (e) {
        setCamError("Camera unavailable — type or scan the code below instead.");
      }
    };

    const t = setTimeout(start, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        try {
          if (s.getState && s.getState() === 2) {
            s.stop()
              .then(() => s.clear())
              .catch(() => {});
          } else {
            s.clear();
          }
        } catch (e) {
          /* ignore */
        }
      }
    };
  }, [open, onDetected, REGION_ID]);

  const submitManual = () => {
    const v = manual.trim();
    if (v) onDetected(v);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="border-zinc-800 bg-zinc-900 text-zinc-100 sm:max-w-md"
        data-testid={`${testPrefix}-dialog`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm uppercase tracking-widest">
            <Barcode size={18} weight="bold" className="text-indigo-300" /> {title}
          </DialogTitle>
        </DialogHeader>
        <div
          id={REGION_ID}
          data-testid={`${testPrefix}-region`}
          className="min-h-[120px] overflow-hidden rounded-lg border border-zinc-800 bg-black"
        />
        {camError && (
          <p
            className="text-xs text-amber-400"
            data-testid={`${testPrefix}-cam-error`}
          >
            {camError}
          </p>
        )}
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-widest text-zinc-500">
            Or type / scan the code
          </p>
          <div className="flex gap-2">
            <Input
              data-testid={`${testPrefix}-manual-input`}
              autoFocus
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitManual();
                }
              }}
              placeholder={placeholder}
              className="h-11 border-zinc-800 bg-zinc-950"
            />
            <Button
              type="button"
              data-testid={`${testPrefix}-manual-add`}
              onClick={submitManual}
              className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              Add
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
